// Cronômetro do round, compartilhado entre abas.
//
// A tela do juiz de round e a aba grande (rota /Cronometro) leem e escrevem o
// MESMO estado, então dá para pausar de qualquer uma das duas — inclusive com a
// barra de espaço, que é o que o juiz alcança sem olhar para a tela.
//
// O estado mora no localStorage (sobrevive a recarregar a página e a abrir a aba
// grande depois que o round já começou) e as mudanças são avisadas por
// BroadcastChannel, com o evento 'storage' como plano B em navegador antigo.
//
// Guardamos o INSTANTE DO FIM, não os segundos restantes: assim a contagem não
// depende de o timer da aba rodar em dia (aba em segundo plano no celular tem o
// setInterval estrangulado pelo navegador) e as duas abas mostram o mesmo número.

// O tempo do round vem da categoria da luta, configurado no evento. Este padrão só
// vale para a aba grande aberta sozinha e para estado gravado por versão anterior.
export const DURACAO_PADRAO_MS = 2 * 60 * 1000

// Largada de um round novo: 3, 2, 1, um por segundo. O relógio começa junto com o
// bip contínuo que vem depois do 1. O som correspondente fica em utils/som.js.
export const CONTAGEM_MS = 3000

const CHAVE = 'cronometro-round'

let canal = null
try {
  canal = new BroadcastChannel('cronometro-round')
} catch (e) {
  canal = null // navegador sem BroadcastChannel: sobra o evento 'storage'
}

export const estadoPadrao = (fightId = 0, duracao = DURACAO_PADRAO_MS) => ({
  fightId,
  duracao,
  rodando: false,
  fimEm: 0, // epoch ms do fim (só vale quando rodando)
  restante: duracao // ms restantes (só vale quando pausado)
})

// Duração total do round deste estado
export const duracaoDe = (estado) => (estado && estado.duracao) || DURACAO_PADRAO_MS

export const ler = () => {
  try {
    const cru = localStorage.getItem(CHAVE)
    return cru ? JSON.parse(cru) : null
  } catch (e) {
    return null
  }
}

export const gravar = (estado) => {
  try {
    localStorage.setItem(CHAVE, JSON.stringify(estado))
  } catch (e) { /* localStorage indisponível: segue só no canal */ }
  if (canal) canal.postMessage(estado)
  return estado
}

// ms restantes de um estado, na hora em que for perguntado
export const restanteDe = (estado) => {
  if (!estado) return DURACAO_PADRAO_MS
  if (!estado.rodando) return Math.max(0, estado.restante)
  // o min segura o relógio parado durante a contagem de largada
  return Math.max(0, Math.min(estado.restante, estado.fimEm - Date.now()))
}

// Número da contagem de largada (3, 2, 1), ou null fora dela.
export const contagemDe = (estado) => {
  if (!estado || !estado.rodando || !estado.largadaEm) return null
  const falta = estado.largadaEm - Date.now()
  if (falta <= 0) return null
  return Math.ceil(falta / 1000)
}

// Round novo passa pela contagem de largada. Continuar de uma pausa volta direto.
export const iniciar = (estado, fightId) => {
  const duracao = duracaoDe(estado)
  const restante = restanteDe(estado) || duracao
  const espera = restante >= duracao ? CONTAGEM_MS : 0
  const agora = Date.now()
  return gravar({
    fightId,
    duracao,
    rodando: true,
    largadaEm: espera ? agora + espera : 0,
    fimEm: agora + espera + restante,
    restante
  })
}

export const pausar = (estado, fightId) => {
  return gravar({ fightId, duracao: duracaoDe(estado), rodando: false, fimEm: 0, restante: restanteDe(estado) })
}

// pausa se estiver correndo, volta do mesmo ponto se estiver parado
export const alternar = (estado, fightId) => {
  if (estado && estado.rodando) return pausar(estado, fightId)
  return iniciar(estado, fightId)
}

export const zerar = (fightId, duracao) => gravar(estadoPadrao(fightId, duracao))

// Deixa o cronômetro pronto para a luta aberta. Luta nova parte do tempo da categoria
// dela, em vez de herdar o que sobrou da anterior. Luta já começada fica como está:
// recarregar a tela ou mudar a configuração não pode mexer num round em andamento.
export const preparar = (estado, fightId, duracao) => {
  if (estado && estado.fightId === fightId) {
    const comecou = estado.rodando || restanteDe(estado) < duracaoDe(estado)
    if (comecou || duracaoDe(estado) === duracao) return estado
  }
  return zerar(fightId, duracao)
}

// Escuta mudanças feitas por qualquer aba. Devolve a função de cancelar.
export const observar = (fn) => {
  const doCanal = (ev) => fn(ev.data)
  const doStorage = (ev) => {
    if (ev.key !== CHAVE || !ev.newValue) return
    try {
      fn(JSON.parse(ev.newValue))
    } catch (e) { /* valor corrompido: ignora */ }
  }
  if (canal) canal.addEventListener('message', doCanal)
  window.addEventListener('storage', doStorage)
  return () => {
    if (canal) canal.removeEventListener('message', doCanal)
    window.removeEventListener('storage', doStorage)
  }
}

export const formatar = (ms) => {
  const total = Math.max(0, Math.ceil(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0')
}
